import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
    TreePalm, 
    Binoculars, 
    Trophy, 
    Users, 
    Leaf, 
    Star, 
    ArrowRight, 
    Heart 
} from '@phosphor-icons/react';

export default function HomePage() {
    const { isAuthenticated } = useAuth();

    const features = [
        {
            icon: <TreePalm weight="duotone" size={48} />,
            title: "Real Missions",
            description: "Complete real-world environmental activities and make a difference!"
        },
        {
            icon: <Star weight="fill" size={48} />,
            title: "Earn Rewards",
            description: "Collect Bush Tokens, badges, and level up from Rookie Ranger to Elite Guardian!"
        },
        {
            icon: <Binoculars weight="duotone" size={48} />,
            title: "Meet Wildlife",
            description: "Learn about amazing Australian animals like koalas, kangaroos, and platypus!"
        },
        {
            icon: <Trophy weight="duotone" size={48} />,
            title: "Leaderboards",
            description: "Compete with rangers across Australia and see your impact!"
        }
    ];

    const impactStats = [
        { number: "10,000+", label: "Trees Planted" },
        { number: "50,000+", label: "Pieces of Rubbish Collected" },
        { number: "25,000+", label: "Young Rangers" },
        { number: "100+", label: "Schools Participating" }
    ];

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 md:h-20">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={24} />
                            </div>
                            <span className="font-fredoka text-xl md:text-2xl font-bold text-[#1e293b]">
                                Bush Rangers
                            </span>
                        </Link>
                        
                        <div className="flex items-center gap-3 md:gap-4">
                            {isAuthenticated ? (
                                <Link to="/dashboard" data-testid="dashboard-link">
                                    <motion.button
                                        whileHover={{ y: -2, x: -2 }}
                                        whileTap={{ y: 2, x: 2 }}
                                        className="brutalist-btn bg-[#22c55e] text-white text-sm md:text-base"
                                    >
                                        My Dashboard
                                    </motion.button>
                                </Link>
                            ) : (
                                <>
                                    <Link to="/login" data-testid="login-link">
                                        <motion.button
                                            whileHover={{ y: -2, x: -2 }}
                                            whileTap={{ y: 2, x: 2 }}
                                            className="brutalist-btn bg-white text-[#1e293b] text-sm md:text-base"
                                        >
                                            Login
                                        </motion.button>
                                    </Link>
                                    <Link to="/register" data-testid="register-link">
                                        <motion.button
                                            whileHover={{ y: -2, x: -2 }}
                                            whileTap={{ y: 2, x: 2 }}
                                            className="brutalist-btn bg-[#22c55e] text-white text-sm md:text-base"
                                        >
                                            Join Now
                                        </motion.button>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="inline-block px-4 py-2 bg-[#fde047] border-4 border-[#1e293b] rounded-full font-fredoka font-semibold text-sm mb-6">
                                Australia's #1 Kids Environmental Platform
                            </span>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-fredoka font-bold text-[#1e293b] leading-tight mb-6">
                                Become a <span className="text-[#22c55e]">Nature Hero</span> Today!
                            </h1>
                            <p className="text-lg md:text-xl text-[#475569] mb-8 leading-relaxed">
                                Join thousands of young Aussie rangers protecting our unique wildlife and environment through fun missions, cool badges, and real-world impact!
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/register" data-testid="hero-join-btn">
                                    <motion.button
                                        whileHover={{ y: -2, x: -2 }}
                                        whileTap={{ y: 2, x: 2 }}
                                        className="brutalist-btn bg-[#22c55e] text-white text-lg w-full sm:w-auto flex items-center justify-center gap-2"
                                    >
                                        Start Your Adventure
                                        <ArrowRight weight="bold" size={24} />
                                    </motion.button>
                                </Link>
                                <Link to="/about" data-testid="hero-learn-btn">
                                    <motion.button
                                        whileHover={{ y: -2, x: -2 }}
                                        whileTap={{ y: 2, x: 2 }}
                                        className="brutalist-btn bg-white text-[#1e293b] text-lg w-full sm:w-auto"
                                    >
                                        Learn More
                                    </motion.button>
                                </Link>
                            </div>
                        </motion.div>
                        
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="brutalist-card overflow-hidden">
                                <img 
                                    src="https://images.unsplash.com/photo-1706066555095-0e021d401e1b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                                    alt="Kangaroo in Australian bush"
                                    className="w-full h-64 md:h-96 object-cover"
                                />
                            </div>
                            {/* Floating Badge */}
                            <motion.div 
                                className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 brutalist-card p-4 bg-[#fde047]"
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                        <Star weight="fill" className="text-white" size={24} />
                                    </div>
                                    <div>
                                        <p className="font-fredoka font-bold text-[#1e293b]">500+ Tokens</p>
                                        <p className="text-sm text-[#475569]">Earned today!</p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 md:py-24 px-4 bg-white border-y-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto">
                    <motion.div 
                        className="text-center mb-12 md:mb-16"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-fredoka font-bold text-[#1e293b] mb-4">
                            How It Works
                        </h2>
                        <p className="text-lg text-[#475569] max-w-2xl mx-auto">
                            Join the adventure and help protect Australia's amazing wildlife and environment!
                        </p>
                    </motion.div>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="brutalist-card p-6 md:p-8 text-center"
                                data-testid={`feature-card-${index}`}
                            >
                                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-[#22c55e]/10 border-4 border-[#1e293b] flex items-center justify-center text-[#22c55e]">
                                    {feature.icon}
                                </div>
                                <h3 className="font-fredoka font-bold text-xl text-[#1e293b] mb-2">
                                    {feature.title}
                                </h3>
                                <p className="text-[#475569]">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Impact Section */}
            <section className="py-16 md:py-24 px-4 bg-[#22c55e]">
                <div className="max-w-7xl mx-auto">
                    <motion.div 
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-fredoka font-bold text-white mb-4">
                            Our Rangers' Impact
                        </h2>
                        <p className="text-lg text-white/90 max-w-2xl mx-auto">
                            Together, young rangers across Australia are making a real difference!
                        </p>
                    </motion.div>
                    
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                        {impactStats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="brutalist-card p-4 md:p-6 text-center bg-white"
                                data-testid={`impact-stat-${index}`}
                            >
                                <p className="font-fredoka font-bold text-2xl md:text-4xl text-[#22c55e] mb-1">
                                    {stat.number}
                                </p>
                                <p className="text-sm md:text-base text-[#475569] font-semibold">
                                    {stat.label}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Australian Animals Section */}
            <section className="py-16 md:py-24 px-4">
                <div className="max-w-7xl mx-auto">
                    <motion.div 
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-fredoka font-bold text-[#1e293b] mb-4">
                            Meet Our Friends
                        </h2>
                        <p className="text-lg text-[#475569] max-w-2xl mx-auto">
                            Learn about the amazing animals you'll help protect!
                        </p>
                    </motion.div>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {[
                            { name: "Koala", image: "https://images.unsplash.com/photo-1459262838948-3e2de6c1ec80?w=400", status: "Vulnerable" },
                            { name: "Kangaroo", image: "https://images.unsplash.com/photo-1706066555095-0e021d401e1b?w=400", status: "Common" },
                            { name: "Platypus", image: "https://images.unsplash.com/photo-1579570806650-b335656b96a9?w=400", status: "Near Threatened" }
                        ].map((animal, index) => (
                            <motion.div
                                key={animal.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.15 }}
                                className="brutalist-card overflow-hidden group"
                                data-testid={`animal-card-${index}`}
                            >
                                <div className="h-48 md:h-56 overflow-hidden">
                                    <img 
                                        src={animal.image}
                                        alt={animal.name}
                                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-4 md:p-6">
                                    <div className="flex justify-between items-center">
                                        <h3 className="font-fredoka font-bold text-xl text-[#1e293b]">
                                            {animal.name}
                                        </h3>
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border-2 border-[#1e293b] ${
                                            animal.status === 'Vulnerable' ? 'bg-[#fb7185] text-white' :
                                            animal.status === 'Near Threatened' ? 'bg-[#fde047] text-[#1e293b]' :
                                            'bg-[#22c55e] text-white'
                                        }`}>
                                            {animal.status}
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 md:py-24 px-4 bg-[#38bdf8]">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-fredoka font-bold text-white mb-6">
                            Ready to Become a Ranger?
                        </h2>
                        <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Join our community of young environmental heroes and start making a difference today!
                        </p>
                        <Link to="/register" data-testid="cta-join-btn">
                            <motion.button
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn bg-white text-[#1e293b] text-lg"
                            >
                                Join Bush Rangers
                                <ArrowRight weight="bold" className="inline ml-2" size={24} />
                            </motion.button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#1e293b] text-white py-12 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-10 h-10 rounded-full bg-[#22c55e] flex items-center justify-center">
                                    <Leaf weight="fill" className="text-white" size={20} />
                                </div>
                                <span className="font-fredoka text-xl font-bold">Bush Rangers</span>
                            </div>
                            <p className="text-gray-400 text-sm">
                                Empowering Australian kids to protect our unique environment through fun and education.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-fredoka font-bold mb-4">Explore</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li><Link to="/missions" className="hover:text-[#22c55e]">Missions</Link></li>
                                <li><Link to="/animals" className="hover:text-[#22c55e]">Animal Library</Link></li>
                                <li><Link to="/leaderboard" className="hover:text-[#22c55e]">Leaderboard</Link></li>
                                <li><Link to="/content" className="hover:text-[#22c55e]">Learn</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-fredoka font-bold mb-4">For Adults</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li><Link to="/about" className="hover:text-[#22c55e]">About Us</Link></li>
                                <li><a href="#" className="hover:text-[#22c55e]">For Schools</a></li>
                                <li><a href="#" className="hover:text-[#22c55e]">For Parents</a></li>
                                <li><a href="#" className="hover:text-[#22c55e]">Partners</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-fredoka font-bold mb-4">Support</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li><a href="#" className="hover:text-[#22c55e]">Contact Us</a></li>
                                <li><a href="#" className="hover:text-[#22c55e]">Privacy Policy</a></li>
                                <li><a href="#" className="hover:text-[#22c55e]">Terms of Service</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-gray-400 text-sm">
                            © 2026 Bush Rangers Australia. Made with <Heart weight="fill" className="inline text-[#fb7185]" size={16} /> for our planet.
                        </p>
                        <div className="flex gap-4">
                            <span className="text-gray-400 text-sm">Proudly Australian</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
